/**
 * Shared chart furniture (ADR-026): the series colour cycle, the section-gutter
 * margin, the designed empty state, the interactive legend and the tooltip body.
 * Every colour in the kit resolves to a chart token through CSS variables - the
 * var() strings survive into the Recharts SVG, so dark and light themes both
 * recolour without re-render. Nothing here invents a hue.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import "./charts.css";

import { strings } from "../brand/strings";

/** How many series colours the palette carries. */
export const SERIES_COUNT = 6;

/** The class for series index i, cycling through the six chart tokens. */
export function seriesClass(index: number): string {
  return `q-chart__c${String((index % SERIES_COUNT) + 1)}`;
}

/**
 * The series colour for index i as a CSS variable reference. Passed to Recharts
 * as fill or stroke; the browser resolves it against the theme's data attributes
 * at paint time, so both themes work from one source of truth.
 */
export function seriesColor(index: number): string {
  return `var(--q-chart-${String((index % SERIES_COUNT) + 1)})`;
}

/** The chart canvas, the same viewBox the hand-drawn kit used. */
export const CHART_SIZE = { width: 640, height: 320 } as const;

/**
 * The chart margins (spec section 4: margins equal the section gutter). The
 * gutter is --q-s0 (16px); top and right carry it exactly. Bottom and left add
 * the room the axis labels and tick text need in the canvas' own coordinates.
 */
export const CHART_GUTTER = 16;
export const CHART_MARGIN = { top: CHART_GUTTER, right: CHART_GUTTER, bottom: 56, left: 56 };

/** The donut's canvas, square as before. */
export const DONUT_SIZE = 240;

/**
 * Recharts routes the className prop to its wrapper div, not the svg; the ref
 * callback runs after every render, so the kit's class lands on the svg itself.
 */
export function surfaceClass(node: SVGSVGElement | null): void {
  node?.classList.add("q-chart__svg");
}

/** The sparkline's canvas, the tile-sized strip as before. */
export const SPARK_SIZE = { width: 120, height: 32 } as const;

/**
 * The props that make one chart mark clickable (the chart contract's drill-down):
 * pointer cursor, keyboard reach, Enter or Space activates. A chart with no natural
 * register target simply never passes an onSelect and its marks stay inert.
 */
export function markClickProps(
  onSelect: (() => void) | undefined,
  extraClass: string,
): {
  readonly className: string;
  readonly tabIndex: 0 | undefined;
  readonly onClick: (() => void) | undefined;
  readonly onKeyDown: ((event: React.KeyboardEvent) => void) | undefined;
} {
  if (onSelect === undefined) {
    return { className: extraClass, tabIndex: undefined, onClick: undefined, onKeyDown: undefined };
  }
  return {
    className: `${extraClass} q-chart__hit`,
    tabIndex: 0,
    onClick: onSelect,
    onKeyDown: (event: React.KeyboardEvent): void => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect();
      }
    },
  };
}

/** The designed empty state. A chart with no data never renders a blank box. */
export function ChartEmpty(): React.JSX.Element {
  return (
    <div className="q-chart__empty" data-testid="chart-empty">
      <p className="q-chart__empty-title">{strings.charts.emptyTitle}</p>
      <p className="q-chart__empty-body">{strings.charts.emptyBody}</p>
    </div>
  );
}

export interface LegendEntry {
  readonly label: string;
  readonly index: number;
  /** Whether the series is currently drawn; false dims the swatch. */
  readonly active?: boolean;
}

/**
 * One legend row per series, the swatch from the same class the marks use. When a
 * chart passes onToggle the legend is interactive (spec section 4: toggle series);
 * otherwise it stays descriptive.
 */
export function Legend({
  entries,
  onToggle,
}: {
  entries: readonly LegendEntry[];
  /** Optional: clicking a legend entry toggles that series on or off. */
  onToggle?: (index: number) => void;
}): React.JSX.Element | null {
  if (entries.length === 0) {
    return null;
  }
  return (
    <ul className="q-chart__legend" aria-label={strings.charts.legend}>
      {entries.map((entry) => {
        const swatch = (
          <span
            className={`q-chart__legend-swatch ${seriesClass(entry.index)}${entry.active === false ? " q-chart__legend-swatch--off" : ""}`}
            aria-hidden="true"
          />
        );
        const body =
          onToggle === undefined ? (
            swatch
          ) : (
            <button
              type="button"
              className={`q-chart__legend-toggle${entry.active === false ? " q-chart__legend-toggle--off" : ""}`}
              aria-pressed={entry.active !== false}
              aria-label={`${strings.charts.toggleSeries}: ${entry.label}`}
              onClick={() => {
                onToggle(entry.index);
              }}
            >
              {swatch}
              {entry.label}
            </button>
          );
        return (
          <li key={entry.label} className="q-chart__legend-item">
            {body}
          </li>
        );
      })}
    </ul>
  );
}

interface TooltipEntry {
  readonly key: string | number;
  readonly name?: string | number;
  readonly value?: string | number | readonly (string | number)[];
  readonly color?: string;
}

/**
 * The tooltip body shared by the charts: the category, then one row per series
 * with its swatch colour, in the theme's tokens.
 */
export function ChartTooltipBody({
  title,
  entries,
}: {
  title: string;
  entries: readonly TooltipEntry[];
}): React.JSX.Element | null {
  if (entries.length === 0) {
    return null;
  }
  return (
    <div className="q-chart__tooltip">
      <p className="q-chart__tooltip-title">{title}</p>
      <ul className="q-chart__tooltip-list">
        {entries.map((entry) => (
          <li key={entry.key} className="q-chart__tooltip-row">
            <span
              aria-hidden="true"
              className="q-chart__tooltip-swatch"
              style={{ background: entry.color }}
            />
            {entry.name === undefined ? "" : `${String(entry.name)}: `}
            {entry.value === undefined ? "" : String(entry.value)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export { strings as chartStrings };
